import { type MutableRefObject, useCallback, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import {
  captureWorkspaceAgentScrollAnchorSnapshot,
  getWorkspaceAgentHistoryPrependScrollDeltaPx,
  getWorkspaceAgentScrollOverflowAnchor,
  getWorkspaceAgentScrollTopAfterFinalHistoryPagePrepended,
  type WorkspaceAgentScrollAnchorSnapshot,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/pinWorkspaceAgentScrollToBottom';

const WORKSPACE_AGENT_HISTORY_SCROLL_RESTORE_MAX_RAF_ATTEMPTS = 10;

type ProgrammaticPinFrameRef = {
  current: number | undefined;
};

export type LoadOlderMessagesOptions = {
  focusComposerAfter?: boolean;
};

type UseWorkspaceAgentMessageHistoryPaginationParams = {
  displayRowCount: number;
  fetchNextMessagesPage: () => Promise<unknown>;
  focusComposer: () => void;
  hasNextMessagesPage: boolean;
  isFetchingNextMessagesPage: boolean;
  isPinnedToBottomRef: MutableRefObject<boolean | undefined>;
  isProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  loadOlderControlBlockRef: MutableRefObject<HTMLDivElement | null>;
  programmaticPinFrameRef: ProgrammaticPinFrameRef;
  scrollContainerRef: MutableRefObject<HTMLElement | null>;
  sessionId: string | null;
  suppressMeasureTailPinRef: MutableRefObject<boolean | undefined>;
  virtualizedTotalSize: number;
};

export type UseWorkspaceAgentMessageHistoryPaginationResult = {
  isLoadingEarlierMessages: boolean;
  loadOlderMessages: (options?: LoadOlderMessagesOptions) => void;
  showLoadOlderMessages: boolean;
};

type LoadOlderUnmountScrollCompensation = {
  heightPx: number;
  scrollTopAtSnapshot: number;
};

const getIsFinalWorkspaceAgentHistoryPageFetch = ({
  hasNextMessagesPage,
  isFinalHistoryPageAfterFetch,
}: {
  hasNextMessagesPage: boolean;
  isFinalHistoryPageAfterFetch: boolean;
}): boolean => isFinalHistoryPageAfterFetch || !hasNextMessagesPage;

const cancelScheduledProgrammaticPinClear = (programmaticPinFrameRef: ProgrammaticPinFrameRef) => {
  if (programmaticPinFrameRef.current === undefined) {
    return;
  }

  cancelAnimationFrame(programmaticPinFrameRef.current);
  programmaticPinFrameRef.current = undefined;
};

const scheduleInstantProgrammaticPinClear = ({
  isProgrammaticPinRef,
  programmaticPinFrameRef,
}: {
  isProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  programmaticPinFrameRef: ProgrammaticPinFrameRef;
}) => {
  cancelScheduledProgrammaticPinClear(programmaticPinFrameRef);

  programmaticPinFrameRef.current = requestAnimationFrame(() => {
    programmaticPinFrameRef.current = requestAnimationFrame(() => {
      programmaticPinFrameRef.current = undefined;
      isProgrammaticPinRef.current = false;
    });
  });
};

const syncWorkspaceAgentScrollOverflowAnchor = ({
  container,
  isPinned,
}: {
  container: HTMLElement;
  isPinned: boolean;
}) => {
  container.style.overflowAnchor = getWorkspaceAgentScrollOverflowAnchor(isPinned);
};

type ApplyHistoryPrependScrollRestoreParams = {
  container: HTMLElement;
  isFinalHistoryPage: boolean;
  isPinnedToBottomRef: MutableRefObject<boolean | undefined>;
  isProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  loadOlderControlBlockHeightPx: number;
  onRestoreComplete: () => void;
  programmaticPinFrameRef: ProgrammaticPinFrameRef;
  snapshot: WorkspaceAgentScrollAnchorSnapshot;
  suppressMeasureTailPinRef: MutableRefObject<boolean | undefined>;
  totalSizeAtSnapshot: number;
  virtualizedTotalSizeRef: MutableRefObject<number>;
};

const applyHistoryPrependScrollRestore = ({
  container,
  isFinalHistoryPage,
  isPinnedToBottomRef,
  isProgrammaticPinRef,
  loadOlderControlBlockHeightPx,
  onRestoreComplete,
  programmaticPinFrameRef,
  snapshot,
  suppressMeasureTailPinRef,
  totalSizeAtSnapshot,
  virtualizedTotalSizeRef,
}: ApplyHistoryPrependScrollRestoreParams) => {
  isProgrammaticPinRef.current = true;
  cancelScheduledProgrammaticPinClear(programmaticPinFrameRef);

  let frozenScrollDeltaPx: number | undefined;

  const applyScrollAnchor = () => {
    if (frozenScrollDeltaPx === undefined) {
      const scrollDeltaPx = getWorkspaceAgentHistoryPrependScrollDeltaPx({
        containerScrollHeight: container.scrollHeight,
        snapshot,
        totalSizeAtSnapshot,
        virtualizedTotalSize: virtualizedTotalSizeRef.current,
      });

      if (scrollDeltaPx <= 0) {
        return false;
      }

      frozenScrollDeltaPx = scrollDeltaPx;
    }

    let nextScrollTop = snapshot.scrollTop + frozenScrollDeltaPx;

    if (isFinalHistoryPage && loadOlderControlBlockHeightPx > 0) {
      nextScrollTop = getWorkspaceAgentScrollTopAfterFinalHistoryPagePrepended({
        loadOlderControlBlockHeightPx,
        restoredScrollTop: nextScrollTop,
        scrollTopAtSnapshot: snapshot.scrollTop,
      });
    }

    container.style.overflowAnchor = 'none';
    container.scrollTop = nextScrollTop;
    return true;
  };

  applyScrollAnchor();

  requestAnimationFrame(() => {
    applyScrollAnchor();
    scheduleInstantProgrammaticPinClear({ isProgrammaticPinRef, programmaticPinFrameRef });
    syncWorkspaceAgentScrollOverflowAnchor({
      container,
      isPinned: isPinnedToBottomRef.current === true,
    });
    onRestoreComplete();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        suppressMeasureTailPinRef.current = false;
      });
    });
  });

  syncWorkspaceAgentScrollOverflowAnchor({
    container,
    isPinned: isPinnedToBottomRef.current === true,
  });
};

export const useWorkspaceAgentMessageHistoryPagination = ({
  displayRowCount,
  fetchNextMessagesPage,
  focusComposer,
  hasNextMessagesPage,
  isFetchingNextMessagesPage,
  isPinnedToBottomRef,
  isProgrammaticPinRef,
  loadOlderControlBlockRef,
  programmaticPinFrameRef,
  scrollContainerRef,
  sessionId,
  suppressMeasureTailPinRef,
  virtualizedTotalSize,
}: UseWorkspaceAgentMessageHistoryPaginationParams): UseWorkspaceAgentMessageHistoryPaginationResult => {
  const messageHistoryVirtualizedTotalSizeRef = useRef(0);
  messageHistoryVirtualizedTotalSizeRef.current = virtualizedTotalSize;

  const [isLoadingEarlierMessages, setIsLoadingEarlierMessages] = useState(false);
  const pendingAnchorSnapshotRef = useRef<WorkspaceAgentScrollAnchorSnapshot | null>(null);
  const displayRowCountAtHistorySnapshotRef = useRef<number | null>(null);
  const virtualizedTotalSizeAtHistorySnapshotRef = useRef<number | null>(null);
  const loadOlderUnmountCompensationRef = useRef<LoadOlderUnmountScrollCompensation | null>(null);
  const shouldFocusComposerAfterHistoryLoadRef = useRef<boolean>(false);
  const wasFetchingNextMessagesPageRef = useRef<boolean>();
  const previousSessionIdRef = useRef<string | null>(null);
  const historyScrollRestoreRafRef = useRef<number | undefined>();
  const historyScrollRestoreRafAttemptRef = useRef(0);
  const historyScrollWaitForRowGrowthAttemptRef = useRef(0);

  const fetchNextMessagesPageRef = useRef(fetchNextMessagesPage);
  fetchNextMessagesPageRef.current = fetchNextMessagesPage;

  const isFetchingNextMessagesPageRef = useRef(isFetchingNextMessagesPage);
  isFetchingNextMessagesPageRef.current = isFetchingNextMessagesPage;

  const hasNextMessagesPageRef = useRef(hasNextMessagesPage);
  hasNextMessagesPageRef.current = hasNextMessagesPage;

  const isFinalHistoryPageAfterFetchRef = useRef<boolean>(false);

  const focusComposerRef = useRef(focusComposer);
  focusComposerRef.current = focusComposer;

  const showLoadOlderMessages = hasNextMessagesPage && displayRowCount > 0;

  const clearPendingHistoryScrollAnchor = useCallback(() => {
    pendingAnchorSnapshotRef.current = null;
    displayRowCountAtHistorySnapshotRef.current = null;
    virtualizedTotalSizeAtHistorySnapshotRef.current = null;
  }, []);

  const maybeFocusComposerAfterHistoryLoad = useCallback(() => {
    if (shouldFocusComposerAfterHistoryLoadRef.current !== true) {
      return;
    }

    shouldFocusComposerAfterHistoryLoadRef.current = false;
    focusComposerRef.current();
  }, []);

  const finishHistoryRestoreCycle = useCallback(() => {
    historyScrollRestoreRafAttemptRef.current = 0;
    historyScrollWaitForRowGrowthAttemptRef.current = 0;

    if (historyScrollRestoreRafRef.current !== undefined) {
      cancelAnimationFrame(historyScrollRestoreRafRef.current);
      historyScrollRestoreRafRef.current = undefined;
    }

    clearPendingHistoryScrollAnchor();
    maybeFocusComposerAfterHistoryLoad();
  }, [clearPendingHistoryScrollAnchor, maybeFocusComposerAfterHistoryLoad]);

  const runFailedOrEmptyFetchCleanup = useCallback(() => {
    finishHistoryRestoreCycle();
    suppressMeasureTailPinRef.current = false;
    cancelScheduledProgrammaticPinClear(programmaticPinFrameRef);
    isProgrammaticPinRef.current = false;

    const container = scrollContainerRef.current;

    if (container !== null) {
      syncWorkspaceAgentScrollOverflowAnchor({
        container,
        isPinned: isPinnedToBottomRef.current !== false,
      });
    }
  }, [
    finishHistoryRestoreCycle,
    isPinnedToBottomRef,
    isProgrammaticPinRef,
    programmaticPinFrameRef,
    scrollContainerRef,
    suppressMeasureTailPinRef,
  ]);

  const loadOlderMessages = useCallback(
    (options?: LoadOlderMessagesOptions) => {
      if (!hasNextMessagesPage || isFetchingNextMessagesPageRef.current) {
        return;
      }

      const container = scrollContainerRef.current;

      if (container === null) {
        return;
      }

      const loadOlderControlBlock = loadOlderControlBlockRef.current;
      const scrollTopAtSnapshot = container.scrollTop;

      loadOlderUnmountCompensationRef.current = {
        heightPx: loadOlderControlBlock?.offsetHeight ?? 0,
        scrollTopAtSnapshot,
      };
      isFinalHistoryPageAfterFetchRef.current = false;
      shouldFocusComposerAfterHistoryLoadRef.current = options?.focusComposerAfter === true;

      pendingAnchorSnapshotRef.current = captureWorkspaceAgentScrollAnchorSnapshot(container);
      displayRowCountAtHistorySnapshotRef.current = displayRowCount;
      virtualizedTotalSizeAtHistorySnapshotRef.current = messageHistoryVirtualizedTotalSizeRef.current;
      isPinnedToBottomRef.current = false;
      suppressMeasureTailPinRef.current = true;
      syncWorkspaceAgentScrollOverflowAnchor({ container, isPinned: false });
      setIsLoadingEarlierMessages(true);
      void fetchNextMessagesPageRef.current();
    },
    [
      displayRowCount,
      hasNextMessagesPage,
      isPinnedToBottomRef,
      loadOlderControlBlockRef,
      scrollContainerRef,
      suppressMeasureTailPinRef,
    ],
  );

  useIsomorphicLayoutEffect(() => {
    if (sessionId !== previousSessionIdRef.current) {
      previousSessionIdRef.current = sessionId;
      wasFetchingNextMessagesPageRef.current = false;
      clearPendingHistoryScrollAnchor();
      loadOlderUnmountCompensationRef.current = null;
      shouldFocusComposerAfterHistoryLoadRef.current = false;
      suppressMeasureTailPinRef.current = false;
      setIsLoadingEarlierMessages(false);
    }

    const wasFetching = wasFetchingNextMessagesPageRef.current === true;
    wasFetchingNextMessagesPageRef.current = isFetchingNextMessagesPage;

    if (!wasFetching || isFetchingNextMessagesPage) {
      return;
    }

    setIsLoadingEarlierMessages(false);

    if (pendingAnchorSnapshotRef.current !== null) {
      // Settled before restore layout runs (same hook, earlier effect) so final-page block compensation applies.
      isFinalHistoryPageAfterFetchRef.current = !hasNextMessagesPage;
    }

    if (pendingAnchorSnapshotRef.current === null) {
      suppressMeasureTailPinRef.current = false;
      maybeFocusComposerAfterHistoryLoad();
    }
  }, [
    clearPendingHistoryScrollAnchor,
    hasNextMessagesPage,
    isFetchingNextMessagesPage,
    maybeFocusComposerAfterHistoryLoad,
    sessionId,
    suppressMeasureTailPinRef,
  ]);

  const restorePendingHistoryScrollAfterPrepend = useCallback(() => {
    const pendingSnapshot = pendingAnchorSnapshotRef.current;
    const rowCountAtSnapshot = displayRowCountAtHistorySnapshotRef.current;
    const totalSizeAtSnapshot = virtualizedTotalSizeAtHistorySnapshotRef.current;

    if (pendingSnapshot === null || rowCountAtSnapshot === null || totalSizeAtSnapshot === null) {
      return;
    }

    const container = scrollContainerRef.current;

    if (container === null) {
      return;
    }

    if (displayRowCount <= rowCountAtSnapshot) {
      if (isFetchingNextMessagesPageRef.current) {
        return;
      }

      historyScrollWaitForRowGrowthAttemptRef.current += 1;

      if (historyScrollWaitForRowGrowthAttemptRef.current < WORKSPACE_AGENT_HISTORY_SCROLL_RESTORE_MAX_RAF_ATTEMPTS) {
        if (historyScrollRestoreRafRef.current !== undefined) {
          cancelAnimationFrame(historyScrollRestoreRafRef.current);
        }

        historyScrollRestoreRafRef.current = requestAnimationFrame(() => {
          historyScrollRestoreRafRef.current = undefined;
          restorePendingHistoryScrollAfterPrepend();
        });

        return;
      }

      runFailedOrEmptyFetchCleanup();
      return;
    }

    historyScrollWaitForRowGrowthAttemptRef.current = 0;

    const scrollDeltaPx = getWorkspaceAgentHistoryPrependScrollDeltaPx({
      containerScrollHeight: container.scrollHeight,
      snapshot: pendingSnapshot,
      totalSizeAtSnapshot,
      virtualizedTotalSize: messageHistoryVirtualizedTotalSizeRef.current,
    });

    if (scrollDeltaPx <= 0) {
      historyScrollRestoreRafAttemptRef.current += 1;

      if (historyScrollRestoreRafAttemptRef.current >= WORKSPACE_AGENT_HISTORY_SCROLL_RESTORE_MAX_RAF_ATTEMPTS) {
        runFailedOrEmptyFetchCleanup();
        return;
      }

      if (historyScrollRestoreRafRef.current !== undefined) {
        cancelAnimationFrame(historyScrollRestoreRafRef.current);
      }

      historyScrollRestoreRafRef.current = requestAnimationFrame(() => {
        historyScrollRestoreRafRef.current = undefined;
        restorePendingHistoryScrollAfterPrepend();
      });

      return;
    }

    historyScrollRestoreRafAttemptRef.current = 0;

    applyHistoryPrependScrollRestore({
      container,
      isFinalHistoryPage: getIsFinalWorkspaceAgentHistoryPageFetch({
        hasNextMessagesPage: hasNextMessagesPageRef.current,
        isFinalHistoryPageAfterFetch: isFinalHistoryPageAfterFetchRef.current,
      }),
      isPinnedToBottomRef,
      isProgrammaticPinRef,
      loadOlderControlBlockHeightPx: loadOlderUnmountCompensationRef.current?.heightPx ?? 0,
      onRestoreComplete: finishHistoryRestoreCycle,
      programmaticPinFrameRef,
      snapshot: pendingSnapshot,
      suppressMeasureTailPinRef,
      totalSizeAtSnapshot,
      virtualizedTotalSizeRef: messageHistoryVirtualizedTotalSizeRef,
    });
  }, [
    displayRowCount,
    finishHistoryRestoreCycle,
    isPinnedToBottomRef,
    isProgrammaticPinRef,
    programmaticPinFrameRef,
    runFailedOrEmptyFetchCleanup,
    scrollContainerRef,
    suppressMeasureTailPinRef,
  ]);

  useIsomorphicLayoutEffect(() => {
    restorePendingHistoryScrollAfterPrepend();
  }, [displayRowCount, restorePendingHistoryScrollAfterPrepend, virtualizedTotalSize]);

  return {
    isLoadingEarlierMessages,
    loadOlderMessages,
    showLoadOlderMessages,
  };
};
